import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { PreArrivalRiskAnalysisRuleModel, VetRunRiskLevelsEnum, VetTaskOutcomeEnum } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { VettingService } from "../../../../../../../services/vetting/vetting.service";
import { RightShipDialogServiceConstants } from "../../../../../../../constants/basics/rightship-dialog.constants";
import { VetRuleOutcomeStepEnum, VettingActionStepEnum } from "../../../../../../../models/vetting/vetting.models";

class OutcomeModel {
    class: string;
    title: string;
}

@autoinject()
@transient()
export class RiskAnalysisRulesDialog {

    rules: PreArrivalRiskAnalysisRuleModel[] = [];
    vesselIdentifier: string;
    vesselName: string;
    vetId: number;
    vhc: boolean;
    categoryName: string;
    filteredRules: PreArrivalRiskAnalysisRuleModel[] = [];
    isLoading: boolean;
    riskFilters: { name: string, isSelected: boolean, count: number, isSatisfied: (item: PreArrivalRiskAnalysisRuleModel) => boolean, order: number }[] = [];
    isSortingByNameAsc: boolean = null;
    isSortingByRiskAsc: boolean = null;
    isSortingByActionAsc: boolean = null;
    detailsExpanded: { [vetTaskId: number]: boolean } = {};

    constructor(private portsAndTerminalsService: PortsAndTerminalsService, private dialogClientService: DialogClientService,
        private vettingService: VettingService) { }

    activate(model: {
        vetId: number, riskCategoryId: number, vesselName: string, vesselIdentifier: string, categoryName: string
        , showPassed: boolean, vhc: boolean
    }) {
        this.isLoading = true;
        this.vetId = model.vetId;
        this.vhc = model.vhc;
        this.vesselName = model.vesselName;
        this.vesselIdentifier = model.vesselIdentifier;
        this.categoryName = model.categoryName;
        this.portsAndTerminalsService.getRiskAnalysisRules({
            vetId: model.vetId,
            riskCategoryId: model.riskCategoryId
        }).then(response => {
            this.isLoading = false;
            this.rules = response.rules;
            const riskFilters = [{
                name: "High Risk",
                count: this.rules.filter(x => !x.isPass && (x.riskLevelId == VetRunRiskLevelsEnum.High || x.riskLevelId == VetRunRiskLevelsEnum.Ora)).length,
                isSelected: false,
                isSatisfied: x => !x.isPass && (x.riskLevelId == VetRunRiskLevelsEnum.High || x.riskLevelId == VetRunRiskLevelsEnum.Ora),
                order: 3
            },
            {
                name: "Medium Risk",
                count: this.rules.filter(x => !x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Medium).length,
                isSelected: false,
                isSatisfied: x => !x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Medium,
                order: 2
            }];

            if (model.showPassed) {
                riskFilters.push({
                    name: "Low Risk",
                    count: this.rules.filter(x => !x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Low).length,
                    isSelected: false,
                    isSatisfied: x => !x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Low,
                    order: 1
                });
                riskFilters.push({
                    name: "Passed",
                    count: this.rules.filter(x => x.isPass).length,
                    isSelected: false,
                    isSatisfied: x => x.isPass,
                    order: 0
                });
            } else {
                riskFilters.push({
                    name: "Low Risk",
                    count: this.rules.filter(x => (!model.vhc && x.isPass) || (!x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Low)).length,
                    isSelected: false,
                    isSatisfied: x => (!model.vhc && x.isPass) || (!x.isPass && x.riskLevelId == VetRunRiskLevelsEnum.Low),
                    order: 1
                });
            }
            this.rules.forEach(r => {
                if (r.vetTaskOutcomeId == VetTaskOutcomeEnum.NA) {
                    r.riskLevel = 'Pending'
                }
                // reassign isPass property value based on VetTaskOutcomeId since there's many case that isPass = true but the actual result is Pending
                r.isPass = r.vetTaskOutcomeId == VetTaskOutcomeEnum.Pass;
            });

            this.riskFilters = riskFilters;
            this.applyFilters();
        });
    }

    openAdditionalDetailsDialog(rule: PreArrivalRiskAnalysisRuleModel) {

        if (rule.hasListItems && !rule.hasRsListItems) {
            this.vettingService.getListItems(rule.vetTaskId).then(listItems => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                    listItems: listItems,
                    task: rule
                }, true, false, false, "right");
            });
        }
        else if (rule.hasCap) {

            this.vettingService.getCapResponse(this.vetId, rule.vetTaskId).then(cap => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                    cap: cap,
                    task: rule
                }, true, false, false, "right");
            });
        }
        else if (rule.canViewDisplayResultsInOverview) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                task: rule
            }, true, false, false, "right");
        }
    }

    toggleRiskFilter(filter) {
        filter.isSelected = !filter.isSelected;
        this.applyFilters();
    }

    clearFilters() {
        this.riskFilters.forEach(x => x.isSelected = false);
        this.applyFilters();
    }

    applyFilters() {
        const selectedFilters = this.riskFilters.filter(x => x.isSelected);

        let query = Enumerable.from(this.rules)
            .where(x => {
                if(this.vhc){
                    return x.vetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail;
                }

                return true;
            })
            .where(x => !selectedFilters || selectedFilters.length == 0 || selectedFilters.some(y => y.isSatisfied(x)))
            .orderByDescending(x => {
                let filters = selectedFilters;
                if (!filters || filters.length == 0) {
                    filters = this.riskFilters;
                }
                const filter = filters.filter(y => y.isSatisfied(x))[0];
                return filter ? filter.order : 0;
            });

        if (this.isSortingByNameAsc !== null) {
            if (this.isSortingByNameAsc) {
                query = query.orderBy(x => x.name.toLowerCase().trim());
            } else {
                query = query.orderByDescending(x => x.name.toLowerCase().trim());
            }
        }
        else if (this.isSortingByRiskAsc !== null) {
            if (this.isSortingByRiskAsc) {
                query = query.orderBy(x => x.riskLevelId);
            } else {
                query = query.orderByDescending(x => x.riskLevelId);
            }
        }
        else if (this.isSortingByActionAsc !== null) {
            if (this.isSortingByActionAsc) {
                query = query.orderBy(x => this.getOutcome(x).title);
            } else {
                query = query.orderByDescending(x => this.getOutcome(x).title);
            }
        }
        else {
            //#DEL-7134
            query = query.orderBy(x => x.isPass ? 1 : 0).thenByDescending(x => x.riskLevelId);
        }


        this.filteredRules = query
            .toArray();
    }

    sortByName() {
        if (this.isSortingByNameAsc === null) {
            this.isSortingByNameAsc = true;
            this.isSortingByActionAsc = null;
            this.isSortingByRiskAsc = null;
        } else if (this.isSortingByNameAsc) {
            this.isSortingByNameAsc = false;
            this.isSortingByActionAsc = null;
            this.isSortingByRiskAsc = null;
        }
        else if (!this.isSortingByNameAsc) {
            this.isSortingByNameAsc = null;
        }
        this.applyFilters();
    }

    sortByRisk() {
        if (this.isSortingByRiskAsc === null) {
            this.isSortingByRiskAsc = true;
            this.isSortingByNameAsc = null;
            this.isSortingByActionAsc = null;
        } else if (this.isSortingByRiskAsc) {
            this.isSortingByRiskAsc = false;
            this.isSortingByActionAsc = null;
            this.isSortingByNameAsc = null;
        }
        else if (!this.isSortingByRiskAsc) {
            this.isSortingByRiskAsc = null;
        }
        this.applyFilters();
    }

    sortByAction() {
        if (this.isSortingByActionAsc === null) {
            this.isSortingByActionAsc = true;
            this.isSortingByRiskAsc = null;
            this.isSortingByNameAsc = null;
        } else if (this.isSortingByActionAsc) {
            this.isSortingByActionAsc = false;
            this.isSortingByRiskAsc = null;
            this.isSortingByNameAsc = null;
        }
        else if (!this.isSortingByActionAsc) {
            this.isSortingByActionAsc = null;
        }
        this.applyFilters();
    }

    expandDetails(vetTaskId: number) {
        if (!this.detailsExpanded) this.detailsExpanded = {};
        this.detailsExpanded[vetTaskId] = Object.keys(this.detailsExpanded).indexOf(vetTaskId.toString()) > -1 ? !this.detailsExpanded[vetTaskId] : true;
    }

    close() {
        this.dialogClientService.hide();
    }

    getOutcome(item: PreArrivalRiskAnalysisRuleModel): OutcomeModel {

        if (item.isCritical && item.ruleActionId && item.ruleActionId != VettingActionStepEnum.VesselHealthCheckCritical)
            return {
                class: "required-actions",
                title: "Actions Required"
            };

        if (!item.isCritical && item.ruleActionId)
            return {
                class: "suggested-actions",
                title: "Actions Suggested"
            };

        if (item.isCritical && item.ruleActionId == VettingActionStepEnum.VesselHealthCheckCritical)
            return {
                class: "failed-critical",
                title: "Critical"
            };

        return {
            class: "no-actions-required",
            title: "Health Check Completed"
        };
    }
}